/**
 * Robberies: with a gun out inside a store (or a bank) the list offers "Rob the register" /
 * "Rob the vault". Cash drips into your pockets for economy.robbery.seconds as long as you stay
 * inside with the gun up; the keeper runs, one star at once, the alarm halfway through
 * (two stars — three at a bank). A place stays robbed for the rest of the game day.
 */
import type { PlacedBuilding } from '../world/buildings';
import { hash01 } from '../world/rng';
import type { Person } from './entities';
import { ECONOMY_DEFAULTS, type Economy } from '../content/types';
import { propertyKey } from './property';
import type { ShopRow } from './shop';


const BANK_TAGS = ['bank'];
const STORE_TAGS = ['shop', 'supermarket', 'gas_station', 'pharmacy', 'bar', 'restaurant', 'fast_food', 'diner', 'cafe', 'bakery', 'deli', 'general_store', 'pawn', 'gun_shop', 'dealer', 'hotel', 'casino', 'cinema', 'laundromat', 'market', 'mall'];

export interface Robbery { key: string; t: number; taken: number; total: number; bank: boolean; alarm: boolean; tick: number; /** a keeper with a shotgun under the counter (person id) */ keeper?: number }

/** Some keepers keep a shotgun under the counter (30 %, hashed per building). */
export function armedKeeper(b: PlacedBuilding, seed: number, chance = ECONOMY_DEFAULTS.robbery.armedKeeperChance): boolean { return hash01(seed + 91, b.x, b.y) < chance; }

export function isBank(b: PlacedBuilding): boolean { return b.def.tags.some((t) => BANK_TAGS.includes(t)); }
export function robbable(b: PlacedBuilding): boolean { return isBank(b) || b.def.tags.some((t) => STORE_TAGS.includes(t)); }

/** What the register / vault holds today (deterministic per building and day). */
export function robTake(b: PlacedBuilding, seed: number, day: number, eco: Economy = ECONOMY_DEFAULTS): number {
  const r = hash01(seed + 77, b.x, b.y, day);
  if (isBank(b)) return eco.robbery.bank[0] + Math.round(r * (eco.robbery.bank[1] - eco.robbery.bank[0]));
  const big = b.def.tags.includes('supermarket') || b.def.tags.includes('casino') || b.def.tags.includes('mall') || b.def.tags.includes('hotel') || b.def.tags.includes('dealer') || b.def.tags.includes('gun_shop');
  const [lo, hi] = eco.robbery.store; const m = big ? eco.robbery.storeBig : 1;
  return Math.round(lo * m + r * (hi - lo) * m);
}

export function gunOut(p: Person): boolean { const w = p.weapons[p.weaponIndex]; return !!w && w.def.class !== 'melee' && w.def.class !== 'thrown'; }

/** The row the list shows: only with a gun out, inside, on the ground floor, not robbed today. */
export function robRow(b: PlacedBuilding, p: Person, seed: number, day: number, robbedDay: number | undefined, eco: Economy = ECONOMY_DEFAULTS): ShopRow | undefined {
  if (!robbable(b) || !gunOut(p) || p.floor || robbedDay === day) return undefined;
  const take = robTake(b, seed, day, eco);
  return { kind: 'rob', id: propertyKey(b), name: isBank(b) ? `Rob the vault — ~$${take} (three stars)` : `Rob the register — ~$${take}`, price: 0 };
}

/** The single row shown while a robbery runs. */
export function robProgressRow(taken: number, total: number): ShopRow { return { kind: 'rob', name: `Robbing… $${taken} of ~$${total} · stay, gun up`, price: 0 }; }
